/**
 * Multi-tenant subdomain routing
 *
 * Vercel serves the SPA on every host (icrop.net, app.icrop.net, <slug>.icrop.net,
 * reserved subdomains). The client resolves the route target before mounting any
 * router — see `App.tsx` / `AppShell`.
 *
 * 3-tier topology (Phase 1):
 *   - `icrop.net` / `www.icrop.net`  → marketing
 *   - `app.icrop.net`                → admin shell
 *   - `<slug>.icrop.net`             → workspace (per-tenant)
 *
 * Reserved subdomains (`api`, `mail`, etc.) render a 404 reserved page.
 *
 * Dev modes:
 *   - `localhost`        → `?ws=<slug>` query param picks the target
 *   - `<slug>.localhost` → first label of the host picks the target
 */

export const RESERVED_SUBDOMAINS = new Set<string>([
  'api',
  'admin',
  'mail',
  'email',
  'send',
  'ftp',
  'smtp',
  'imap',
  'dev',
  'staging',
  'test',
]);

// `www` → marketing and `app` → admin are handled explicitly above the reserved
// set; they are not workspaces.

export const SLUG_REGEX = /^[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$/;

export type RouteTarget =
  | { kind: 'marketing' }
  | { kind: 'admin' }
  | { kind: 'workspace'; slug: string }
  | { kind: 'reserved'; sub: string };

function getApexDomain(): string {
  let domain = (import.meta.env.VITE_APP_DOMAIN as string | undefined) || 'icrop.net';
  // VITE_APP_DOMAIN must be the apex (e.g. icrop.net). Tolerate it being set to
  // the app subdomain (app.icrop.net) by stripping a leading "app." label —
  // otherwise buildAdminUrl/buildWorkspaceUrl produce app.app.icrop.net.
  if (domain.startsWith('app.')) domain = domain.slice(4);
  return domain;
}

function classifySubdomain(sub: string): RouteTarget {
  if (sub === 'www') return { kind: 'marketing' };
  if (sub === 'app') return { kind: 'admin' };
  if (RESERVED_SUBDOMAINS.has(sub)) return { kind: 'reserved', sub };
  if (!SLUG_REGEX.test(sub)) return { kind: 'reserved', sub };
  return { kind: 'workspace', slug: sub };
}

export function resolveRoute(): RouteTarget {
  const host = window.location.hostname.toLowerCase();

  // localhost dev: `?ws=<slug>` selects the target
  if (host === 'localhost' || host.startsWith('127.')) {
    const sub = new URLSearchParams(window.location.search).get('ws');
    if (!sub) return { kind: 'marketing' };
    return classifySubdomain(sub.toLowerCase());
  }

  // `<slug>.localhost` dev subdomains (e.g. acme.localhost:5173)
  if (host.endsWith('.localhost')) {
    const sub = host.split('.')[0];
    if (!sub) return { kind: 'marketing' };
    return classifySubdomain(sub);
  }

  // Prod: anything with fewer than three labels is the apex
  const parts = host.split('.');
  if (parts.length < 3) return { kind: 'marketing' };

  // Guard: only classify as workspace/admin if the host actually ends with the
  // configured apex domain. Non-apex hosts (vercel.app, netlify.app, etc.) fall
  // through to marketing so Vercel preview deploys don't resolve as workspace slugs.
  // The guard is active only when VITE_APP_DOMAIN is set to a real production domain
  // (i.e. contains a dot but no port/localhost). When running locally without a real
  // domain configured the default fallback 'icrop.net' still applies correctly.
  const apex = getApexDomain();
  const apexLooksReal = apex.includes('.') && !apex.includes(':') && !apex.includes('localhost');
  if (apexLooksReal && !host.endsWith('.' + apex) && host !== apex) return { kind: 'marketing' };

  return classifySubdomain(parts[0]);
}

export function buildWorkspaceUrl(slug: string, path = '/dashboard'): string {
  const apex = getApexDomain();
  const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
  return `${protocol}//${slug}.${apex}${path}`;
}

export function buildAdminUrl(path = '/admin'): string {
  const apex = getApexDomain();
  const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
  return `${protocol}//app.${apex}${path}`;
}

export function buildMarketingUrl(path = '/'): string {
  const apex = getApexDomain();
  const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
  return `${protocol}//${apex}${path}`;
}
